/**
 * DaySelector.js - Horizontal day picker for mobile views
 * 
 * Features:
 * - Horizontal scrollable pill/tab selector (Mon-Fri)
 * - Touch-friendly tap targets (44px+ height)
 * - Visual indicator for selected day
 * - Today highlight
 * - Compact date display
 * 
 * Usage:
 *   <DaySelector 
 *     weekDates={['2025-12-01', '2025-12-02', ...]}
 *     selectedDate={selectedDate}
 *     onSelectDate={setSelectedDate}
 *   />
 */

import React from 'react';
import { useTheme } from '../context/ThemeContext';

function DaySelector({ weekDates, selectedDate, onSelectDate }) {
  const { theme, colors, darkMode } = useTheme();

  // Get today's date string for comparison
  const getTodayStr = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const todayStr = getTodayStr();

  // Format day name (short)
  const formatDayShort = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short' });
  };

  // Format day number
  const formatDayNum = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    return d.getDate();
  };

  // =============================================
  // STYLES
  // =============================================

  const containerStyle = {
    display: 'flex',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: theme.cardBackground,
    borderRadius: '12px',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch', // Smooth scroll on iOS
    scrollbarWidth: 'none', // Hide scrollbar Firefox
    msOverflowStyle: 'none', // Hide scrollbar IE/Edge
    justifyContent: 'space-between', // Distribute days evenly
  };

  const dayButtonStyle = (dateStr) => {
    const isSelected = dateStr === selectedDate;
    const isToday = dateStr === todayStr;
    
    return {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      flex: '1 1 0', // Equal width for all buttons
      minWidth: '48px',
      maxWidth: '72px',
      height: '64px',
      padding: '8px 4px',
      border: isToday && !isSelected 
        ? `2px solid ${colors.blue}` 
        : '2px solid transparent',
      borderRadius: '12px',
      backgroundColor: isSelected 
        ? colors.blue 
        : theme.backgroundSecondary,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    };
  };

  const dayNameStyle = (dateStr) => {
    const isSelected = dateStr === selectedDate;
    
    return {
      fontSize: '0.75rem',
      fontWeight: '600',
      color: isSelected 
        ? (darkMode ? '#1a1a2e' : '#fff')
        : theme.textSecondary,
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    };
  };

  const dayNumStyle = (dateStr) => {
    const isSelected = dateStr === selectedDate;
    
    return {
      fontSize: '1.25rem',
      fontWeight: '700',
      color: isSelected 
        ? (darkMode ? '#1a1a2e' : '#fff')
        : theme.text,
      marginTop: '2px'
    };
  };

  // =============================================
  // RENDER
  // =============================================

  return (
    <div 
      style={containerStyle}
      className="day-selector-hide-scrollbar"
    >
      {/* Inline style to hide scrollbar in webkit browsers */}
      <style>
        {`
          .day-selector-hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
      
      {weekDates.map((dateStr) => (
        <button
          key={dateStr}
          style={dayButtonStyle(dateStr)}
          onClick={() => onSelectDate(dateStr)}
          aria-label={`Select ${formatDayShort(dateStr)}`}
          aria-pressed={dateStr === selectedDate}
        >
          <span style={dayNameStyle(dateStr)}>
            {formatDayShort(dateStr)}
          </span>
          <span style={dayNumStyle(dateStr)}>
            {formatDayNum(dateStr)}
          </span>
        </button>
      ))}
    </div>
  );
}

export default DaySelector;
